import {
  collection,
  doc,
  runTransaction,
  serverTimestamp,
  Timestamp,
  type Firestore,
} from 'firebase/firestore'
import { firestoreDb } from './firebase'
import { buildAuditSearchPrefixes } from './auditLedgerService'
import { STOCK_COLLECTION, type StockItem } from '../types/stock'
import { toStockItem } from './stockService'
import { findStockItemForBarcode } from './stockCountSuggestionService'

export type StockScanDirection = 'IN' | 'OUT'
export type StockScanTargetType = 'WORKSPACE' | 'GOAL' | 'TASK' | 'SUBTASK' | 'CHECKLIST'

export type StockAction = {
  workspaceId: string
  sku: string
  action: StockScanDirection
  taskId?: string
  checklistItemId?: string
  targetType?: StockScanTargetType
  targetId?: string
  targetTitle?: string
  actorId: string
  actorName: string
  timestamp: number
}

export type InventoryItem = {
  sku: string
  barcode: string
  workspaceId: string
  name: string
  quantityOnHand: number
  reservedQuantity: number
  capacity: number
  unit: string
}

export type ResourceRequirementStatus = 'PENDING' | 'READY' | 'CONSUMED' | 'MISMATCH'

export type ResourceRequirement = {
  workspaceId: string
  elementId: string
  checklistItemId: string
  sku: string
  quantityRequired: number
  fulfilledQuantity: number
  requiredItemName?: string
  status: ResourceRequirementStatus
}

export type InventoryScanResult = {
  item: InventoryItem
  action: StockScanDirection
  quantityDelta: number
  checklistItemId?: string
}

export class ResourceMismatchError extends Error {
  readonly code = 'RESOURCE_MISMATCH'
  readonly itemName: string
  readonly taskName: string
  readonly requiredItemName: string

  constructor(itemName: string, taskName: string, requiredItemName: string) {
    super(`Mismatch: Scanned ${itemName} but ${taskName} requires ${requiredItemName}.`)
    this.name = 'ResourceMismatchError'
    this.itemName = itemName
    this.taskName = taskName
    this.requiredItemName = requiredItemName
  }
}

export class InventoryScanError extends Error {
  readonly code: 'INVENTORY_NOT_FOUND' | 'INSUFFICIENT_STOCK' | 'TASK_REQUIRED' | 'REQUIREMENT_NOT_FOUND'

  constructor(code: InventoryScanError['code'], message: string) {
    super(message)
    this.name = 'InventoryScanError'
    this.code = code
  }
}

const sanitizeCount = (value: unknown): number => Math.max(0, Math.floor(Number(value) || 0))

const requirementRef = (db: Firestore, elementId: string, requirementId: string) =>
  doc(db, 'elements', elementId, 'resourceRequirements', requirementId)

export const toInventoryItem = (item: StockItem): InventoryItem => ({
  sku: item.sku || item.id,
  barcode: item.barcode ?? '',
  workspaceId: item.workspaceId,
  name: item.name || item.sku || item.id,
  quantityOnHand: item.quantity,
  reservedQuantity: item.reservedQuantity,
  capacity: 0,
  unit: item.unit ?? '',
})

const normalizeRequirement = (data: Record<string, unknown>): ResourceRequirement => ({
  workspaceId: typeof data.workspaceId === 'string' ? data.workspaceId : '',
  elementId: typeof data.elementId === 'string' ? data.elementId : '',
  checklistItemId: typeof data.checklistItemId === 'string' ? data.checklistItemId : '',
  sku: typeof data.sku === 'string' ? data.sku : '',
  quantityRequired: Math.max(1, Math.floor(Number(data.quantityRequired) || 1)),
  fulfilledQuantity: sanitizeCount(data.fulfilledQuantity),
  requiredItemName: typeof data.requiredItemName === 'string' ? data.requiredItemName : undefined,
  status: data.status === 'READY' || data.status === 'CONSUMED' || data.status === 'MISMATCH' ? data.status : 'PENDING',
})

const buildActivity = (
  action: StockAction,
  item: InventoryItem,
  activityAction: 'INVENTORY_SCANNED_IN' | 'INVENTORY_SCANNED_OUT' | 'INVENTORY_ERROR',
  summary: string,
  metadata: Record<string, unknown>,
) => ({
  workspaceId: action.workspaceId,
  actorId: action.actorId,
  actorName: action.actorName,
  targetType: 'INVENTORY',
  nodeType: 'INVENTORY',
  targetId: item.sku,
  targetTitle: item.name,
  action: activityAction,
  actionType: activityAction,
  activityType: activityAction === 'INVENTORY_ERROR' ? 'OTHER' : 'UPDATE',
  summary,
  board: 'DASHBOARD',
  metadata,
  maskedPayload: metadata,
  snapshot: null,
  auditSearchPrefixes: buildAuditSearchPrefixes(`${item.name} ${item.sku} ${activityAction}`),
  retentionMode: 'STANDARD',
  createdAt: serverTimestamp(),
  expiresAt: Timestamp.fromMillis(Date.now() + 2 * 24 * 60 * 60 * 1000),
})

const writeInventoryErrorAudit = async (
  db: Firestore,
  action: StockAction,
  item: InventoryItem,
  summary: string,
  metadata: Record<string, unknown>,
) => {
  await runTransaction(db, async (transaction) => {
    transaction.set(doc(collection(db, 'workspace_activity')), buildActivity(action, item, 'INVENTORY_ERROR', summary, metadata))
  })
}

export const processInventoryScan = async (
  action: StockAction,
  db: Firestore = firestoreDb,
): Promise<InventoryScanResult> => {
  const scannedCode = action.sku.trim()
  if (!action.workspaceId || !scannedCode) throw new InventoryScanError('INVENTORY_NOT_FOUND', 'Scan did not include a barcode.')
  const match = await findStockItemForBarcode(action.workspaceId, scannedCode, db)
  if (!match) throw new InventoryScanError('INVENTORY_NOT_FOUND', `No inventory item found for ${scannedCode}.`)

  const itemRef = doc(db, STOCK_COLLECTION, match.id)
  const elementRef = action.taskId ? doc(db, 'elements', action.taskId) : null
  const checklistRef = action.checklistItemId ? doc(db, 'checklists', action.checklistItemId) : null
  const requirement = action.taskId && action.checklistItemId
    ? {
      ref: requirementRef(db, action.taskId, action.checklistItemId),
      id: action.checklistItemId,
    }
    : null

  let errorAudit: { item: InventoryItem; summary: string; metadata: Record<string, unknown> } | null = null
  try {
    return await runTransaction(db, async (transaction) => {
      const itemSnap = await transaction.get(itemRef)
      if (!itemSnap.exists()) throw new InventoryScanError('INVENTORY_NOT_FOUND', `No inventory item found for ${scannedCode}.`)
      const stockItem = toStockItem(itemSnap.id, itemSnap.data())
      const item = toInventoryItem(stockItem)
      const quantityOnHand = item.quantityOnHand

      let taskName = 'this task'
      let requirementData: ResourceRequirement | null = null
      if (action.action === 'OUT') {
        const [elementSnap, checklistSnap, requirementSnap] = await Promise.all([
          elementRef ? transaction.get(elementRef) : Promise.resolve(null),
          checklistRef ? transaction.get(checklistRef) : Promise.resolve(null),
          requirement ? transaction.get(requirement.ref) : Promise.resolve(null),
        ])
        taskName = typeof elementSnap?.data()?.title === 'string'
          ? String(elementSnap.data()?.title)
          : action.targetTitle || taskName
        if (checklistRef && requirement) {
          if (!checklistSnap?.exists()) throw new InventoryScanError('REQUIREMENT_NOT_FOUND', 'Checklist item no longer exists.')
          if (requirementSnap?.exists()) {
            requirementData = normalizeRequirement(requirementSnap.data() as Record<string, unknown>)
            const requiredName = requirementData.requiredItemName ?? requirementData.sku
            if (requirementData.workspaceId !== action.workspaceId || requirementData.sku !== item.sku) {
              errorAudit = { item, summary: 'Inventory scan mismatch.', metadata: {
                taskId: action.taskId,
                checklistItemId: action.checklistItemId,
                targetType: action.targetType ?? null,
                targetId: action.targetId ?? null,
                scannedSku: item.sku,
                scannedBarcode: scannedCode,
                requiredSku: requirementData.sku,
                requiredItemName: requiredName,
                reason: 'RESOURCE_MISMATCH',
              } }
              throw new ResourceMismatchError(item.name, taskName, requiredName)
            }
          }
        }
        if (quantityOnHand < 1) {
          errorAudit = { item, summary: 'Inventory scan failed: insufficient stock.', metadata: {
            taskId: action.taskId,
            checklistItemId: action.checklistItemId,
            targetType: action.targetType ?? null,
            targetId: action.targetId ?? null,
            scannedSku: item.sku,
            scannedBarcode: scannedCode,
            quantityOnHand,
            reason: 'INSUFFICIENT_STOCK',
          } }
          throw new InventoryScanError('INSUFFICIENT_STOCK', `Insufficient Stock: ${item.name}.`)
        }
      }

      // NOTE: processInventoryScan no longer writes stock to workspace_stock (FI4.a).
      if (action.action === 'OUT' && requirement && checklistRef && requirementData) {
        transaction.update(requirement.ref, {
          status: 'READY',
          fulfilledQuantity: requirementData.fulfilledQuantity + 1,
          updatedAt: serverTimestamp(),
          lastScanId: `${action.actorId}-${action.timestamp}`,
        })
        transaction.update(checklistRef, {
          isReady: true,
          resourceReadySku: item.sku,
          resourceReadyAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          lastUpdatedBy: action.actorId,
          lastUpdatedByName: action.actorName,
        })
      }

      const activityAction = action.action === 'IN' ? 'INVENTORY_SCANNED_IN' : 'INVENTORY_SCANNED_OUT'
      transaction.set(doc(collection(db, 'workspace_activity')), buildActivity(
        action,
        item,
        activityAction,
        action.action === 'IN'
          ? `Inventory scanned in: ${item.name}.`
          : `Inventory scanned out for ${taskName}: ${item.name}.`,
        {
          taskId: action.taskId ?? null,
          checklistItemId: action.checklistItemId ?? null,
          targetType: action.targetType ?? null,
          targetId: action.targetId ?? null,
          targetTitle: action.targetTitle ?? null,
          scannedSku: item.sku,
          scannedBarcode: scannedCode,
          quantityDelta: action.action === 'IN' ? 1 : -1,
          quantityBefore: quantityOnHand,
          quantityAfter: quantityOnHand,
        },
      ))

      return {
        item,
        action: action.action,
        quantityDelta: action.action === 'IN' ? 1 : -1,
        checklistItemId: action.checklistItemId,
      }
    })
  } catch (error) {
    const capturedAudit = errorAudit as { item: InventoryItem; summary: string; metadata: Record<string, unknown> } | null
    if (capturedAudit) {
      await writeInventoryErrorAudit(db, action, capturedAudit.item, capturedAudit.summary, capturedAudit.metadata).catch((auditError) => {
        console.warn('[inventory] error audit failed', auditError)
      })
    }
    throw error
  }
}
